import { Classes, Colors } from "@blueprintjs/core";
import {
  FieldSet,
  IDataTableColumn,
  ISearchCriteriaValues,
  SortKeysObject,
  useGridState,
  useSearchApi,
} from "nsitools-react";
import * as React from "react";
import { css } from "styled-components";

import { CustomDataTable } from "../..";

interface ICustomGridSelectorProps<TResults> {
  idField: string;
  errorFields?: string[];
  searchFunction: (sObj: ISearchCriteriaValues) => TResults | Promise<TResults>;
  columns: Array<IDataTableColumn>;
  selectionChanged: (ids: any[]) => void;
  selectedIds: any[];
  currentCountChanged: (count: number) => void;
  title: string;
  sortKeys?: SortKeysObject;
  isErrorSelected?: (isError: boolean) => void;
}

export function SRSHCustomGridSelector<TResults>(props: ICustomGridSelectorProps<TResults>) {
  const {
    idField,
    searchFunction,
    columns,
    selectionChanged,
    selectedIds,
    currentCountChanged,
    title,
    sortKeys,
    errorFields,
    isErrorSelected,
  } = props;

  const tableState = useGridState<any>({
    serverMode: true,
    availablePageSizes: [10],
    enablePagination: true,
    enableFilter: true,
    sortKeys: sortKeys,
  });

  const { search, loading, gridResults } = useSearchApi(
    React.useMemo(
      () => ({
        searchFunction,
        tableState,
        initialSearch: false,
        trimSearchObjectStringValues: true,
      }),
      [searchFunction, tableState],
    ),
  );

  React.useEffect(() => {
    if (isErrorSelected && errorFields && gridResults) {
      const results = gridResults["results"];

      !results
        ? isErrorSelected(false)
        : isErrorSelected(results.find((r) => errorFields.find((field) => r[field])) !== undefined);
    }
  }, [errorFields, gridResults, isErrorSelected]);

  React.useEffect(() => {
    if (searchFunction) {
      search();
    }
  }, [search, searchFunction]);

  const selectRow = React.useCallback(
    (item: any) => {
      if (selectedIds.find((d) => d === item[idField])) {
        selectionChanged(selectedIds.filter((d) => d !== item[idField]));
      } else {
        selectionChanged(selectedIds.concat([item[idField]]));
      }
    },
    [idField, selectedIds, selectionChanged],
  );

  const { totalCount } = tableState;

  React.useEffect(() => currentCountChanged(totalCount), [currentCountChanged, totalCount]);

  const customizeRowStyleCallback = React.useCallback(
    (d: any) => {
      const isSelected = selectedIds.includes(d[idField]);
      const error = errorFields && errorFields.find((field) => d[field]);
      return css`
        & * {
          color: ${error
            ? isSelected
              ? Colors.RED5
              : "#c23030"
            : isSelected
            ? Colors.WHITE
            : Colors.BLACK} !important;
        }

        & td {
          vertical-align: middle !important;

          & .${Classes.CONTROL} {
            margin: 10px;
          }
        }
        background-color: ${isSelected ? Colors.BLUE1 : "transparent"};
      `;
    },
    [errorFields, idField, selectedIds],
  );

  return (
    <FieldSet title={title}>
      <CustomDataTable
        tableState={tableState}
        columns={columns}
        loading={loading}
        onRowClick={selectRow}
        customizeRowStyle={customizeRowStyleCallback}
        filterMode="OnEnter"
      />
    </FieldSet>
  );
}
